import { User, LoginCredentials, LoginResponse } from '@rfacil/shared/types';

/**
 * @interface ApiResponse
 * @description Define la forma de la respuesta de la API.
 * @template T - El tipo de los datos de la respuesta.
 */
interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  success: boolean;
  errors?: string[];
}

/**
 * @interface Invoice
 * @description Define la forma de la factura.
 * @property {string} id - El ID de la factura.
 * @property {string} number - El número de la factura.
 * @property {Date} date - La fecha de la factura.
 * @property {number} amount - El monto de la factura.
 * @property {string} status - El estado de la factura.
 * @property {string} client - El cliente de la factura.
 */
interface Invoice {
  id: string;
  number: string;
  date: Date;
  amount: number;
  status: string;
  client: string;
}

/**
 * @interface Expense
 * @description Define la forma del gasto.
 * @property {string} id - El ID del gasto.
 * @property {string} description - La descripción del gasto.
 * @property {number} amount - El monto del gasto.
 * @property {Date} date - La fecha del gasto.
 * @property {string} category - La categoría del gasto.
 * @property {string} status - El estado del gasto.
 * @property {string} approver - El aprobador del gasto.
 */
interface Expense {
  id: string;
  description: string;
  amount: number;
  date: Date;
  category: string;
  status: string;
  approver?: string;
}

/**
 * @interface Provider
 * @description Define la forma del proveedor.
 * @property {string} id - El ID del proveedor.
 * @property {string} name - El nombre del proveedor.
 * @property {string} email - El email del proveedor.
 * @property {string} phone - El teléfono del proveedor.
 * @property {string} address - La dirección del proveedor.
 * @property {string} status - El estado del proveedor.
 */
interface Provider {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  status: string;
}

/**
 * @constant API_CONSTANTS
 * @description Define las constantes de la API.
 * @property {string} BASE_URL - La URL base de la API.
 * @property {object} ENDPOINTS - Los endpoints de la API.
 * @property {number} TIMEOUT - El tiempo de espera de la API.
 */
const API_CONSTANTS = {
  BASE_URL: 'http://localhost:3001',
  ENDPOINTS: {
    AUTH: {
      LOGIN: '/auth/login',
      LOGOUT: '/auth/logout',
      REFRESH: '/auth/refresh',
      PROFILE: '/auth/profile',
    },
    INVOICES: {
      LIST: '/invoices',
      CREATE: '/invoices',
      UPDATE: '/invoices/:id',
      DELETE: '/invoices/:id',
    },
    EXPENSES: {
      LIST: '/expenses',
      CREATE: '/expenses',
      UPDATE: '/expenses/:id',
      DELETE: '/expenses/:id',
      APPROVE: '/expenses/:id/approve',
    },
    PROVIDERS: {
      LIST: '/providers',
      CREATE: '/providers',
      UPDATE: '/providers/:id',
      DELETE: '/providers/:id',
    },
  },
  TIMEOUT: 10000,
};

/**
 * @class BaseService
 * @description Una clase base para crear clientes de servicios API.
 * Proporciona funcionalidad común para realizar solicitudes HTTP, incluyendo la configuración de la URL base,
 * cabeceras por defecto y el manejo de tokens de autenticación.
 */
class BaseService {
  protected baseURL: string;
  private defaultHeaders: Record<string, string>;

  constructor() {
    this.baseURL = API_CONSTANTS.BASE_URL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  private getAuthToken(): string | null {
    try {
      return localStorage.getItem('rfacil_token');
    } catch {
      return null;
    }
  }

  private async request<T>(url: string, config: RequestInit = {}): Promise<T> {
    const token = this.getAuthToken();
    const headers = {
      ...this.defaultHeaders,
      ...(token && { Authorization: `Bearer ${token}` }),
      ...config.headers,
    };

    const fullUrl = `${this.baseURL}${url}`;
    const requestConfig: RequestInit = {
      ...config,
      headers,
      signal: AbortSignal.timeout(API_CONSTANTS.TIMEOUT),
    };

    try {
      const response = await fetch(fullUrl, requestConfig);
      const data = await response.json();

      if (!response.ok) {
        const errorData = data as { message?: string };
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  protected async get<T>(url: string): Promise<T> {
    return this.request<T>(url, { method: 'GET' });
  }

  protected async post<T>(url: string, data?: unknown): Promise<T> {
    return this.request<T>(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  protected async put<T>(url: string, data?: unknown): Promise<T> {
    return this.request<T>(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  protected async patch<T>(url: string, data?: unknown): Promise<T> {
    return this.request<T>(url, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  protected async delete<T>(url: string): Promise<T> {
    return this.request<T>(url, { method: 'DELETE' });
  }
}

/**
 * @class AuthService
 * @extends BaseService
 * @description Un servicio para manejar solicitudes a la API relacionadas con la autenticación, como el inicio y cierre de sesión.
 */
export class AuthService extends BaseService {
  constructor() {
    super();
    // Actualizar la URL base para usar el endpoint de desarrollo
    this.baseURL = 'https://localhost:7260/api';
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await this.post<LoginResponse>('/Login/login', credentials);
      return response;
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    // En el backend real, podrías tener un endpoint de logout
    // Por ahora, simplemente retornamos éxito
    return Promise.resolve();
  }

  async refreshToken(): Promise<{ token: string }> {
    // Implementar lógica de refresh token si es necesaria
    throw new Error('Refresh token no implementado aún');
  }

  async getProfile(): Promise<User> {
    // Implementar obtención del perfil del usuario
    throw new Error('Get profile no implementado aún');
  }
}

/**
 * @class AutofacturaService
 * @extends BaseService
 * @description Un servicio para manejar solicitudes a la API relacionadas con facturas.
 */
export class AutofacturaService extends BaseService {
  /**
   * @method getInvoices
   * @description Recupera una lista de facturas.
   * @param {object} [params] - Parámetros de consulta opcionales.
   * @param {number} [params.page] - El número de página.
   * @param {number} [params.limit] - El número de artículos por página.
   * @param {string} [params.status] - El estado de las facturas por el que filtrar.
   * @param {string} [params.client] - El cliente por el que filtrar.
   * @returns {Promise<ApiResponse<Invoice[]>>} - Una promesa que se resuelve con la lista de facturas.
   */
  async getInvoices(params?: {
    page?: number;
    limit?: number;
    status?: string;
    client?: string;
  }): Promise<ApiResponse<Invoice[]>> {
    const queryString = params ? `?${new URLSearchParams(params as Record<string, string>).toString()}` : '';
    return this.get(`${API_CONSTANTS.ENDPOINTS.INVOICES.LIST}${queryString}`);
  }

  async getInvoice(id: string): Promise<ApiResponse<Invoice>> {
    return this.get(`${API_CONSTANTS.ENDPOINTS.INVOICES.LIST}/${id}`);
  }

  async createInvoice(invoice: Omit<Invoice, 'id'>): Promise<ApiResponse<Invoice>> {
    return this.post(API_CONSTANTS.ENDPOINTS.INVOICES.CREATE, invoice);
  }

  async updateInvoice(id: string, invoice: Partial<Invoice>): Promise<ApiResponse<Invoice>> {
    return this.put(`${API_CONSTANTS.ENDPOINTS.INVOICES.UPDATE.replace(':id', id)}`, invoice);
  }

  async deleteInvoice(id: string): Promise<ApiResponse<void>> {
    return this.delete(`${API_CONSTANTS.ENDPOINTS.INVOICES.DELETE.replace(':id', id)}`);
  }
}

/**
 * @class GastosService
 * @extends BaseService
 * @description Un servicio para manejar solicitudes a la API relacionadas con gastos.
 */
export class GastosService extends BaseService {
  /**
   * @method getExpenses
   * @description Recupera una lista de gastos.
   * @param {object} [params] - Parámetros de consulta opcionales.
   * @param {number} [params.page] - El número de página.
   * @param {number} [params.limit] - El número de artículos por página.
   * @param {string} [params.status] - El estado de los gastos por el que filtrar.
   * @param {string} [params.category] - La categoría por la que filtrar.
   * @returns {Promise<ApiResponse<Expense[]>>} - Una promesa que se resuelve con la lista de gastos.
   */
  async getExpenses(params?: {
    page?: number;
    limit?: number;
    status?: string;
    category?: string;
  }): Promise<ApiResponse<Expense[]>> {
    const queryString = params ? `?${new URLSearchParams(params as Record<string, string>).toString()}` : '';
    return this.get(`${API_CONSTANTS.ENDPOINTS.EXPENSES.LIST}${queryString}`);
  }

  async getExpense(id: string): Promise<ApiResponse<Expense>> {
    return this.get(`${API_CONSTANTS.ENDPOINTS.EXPENSES.LIST}/${id}`);
  }

  async createExpense(expense: Omit<Expense, 'id'>): Promise<ApiResponse<Expense>> {
    return this.post(API_CONSTANTS.ENDPOINTS.EXPENSES.CREATE, expense);
  }

  async updateExpense(id: string, expense: Partial<Expense>): Promise<ApiResponse<Expense>> {
    return this.put(`${API_CONSTANTS.ENDPOINTS.EXPENSES.UPDATE.replace(':id', id)}`, expense);
  }

  async deleteExpense(id: string): Promise<ApiResponse<void>> {
    return this.delete(`${API_CONSTANTS.ENDPOINTS.EXPENSES.DELETE.replace(':id', id)}`);
  }

  async approveExpense(id: string): Promise<ApiResponse<Expense>> {
    return this.patch(`${API_CONSTANTS.ENDPOINTS.EXPENSES.APPROVE.replace(':id', id)}`);
  }
}

/**
 * @class ProveedoresService
 * @extends BaseService
 * @description Un servicio para manejar solicitudes a la API relacionadas con proveedores.
 */
export class ProveedoresService extends BaseService {
  /**
   * @method getProviders
   * @description Recupera una lista de proveedores.
   * @param {object} [params] - Parámetros de consulta opcionales.
   * @param {number} [params.page] - El número de página.
   * @param {number} [params.limit] - El número de artículos por página.
   * @param {string} [params.status] - El estado de los proveedores por el que filtrar.
   * @param {string} [params.search] - Un término de búsqueda por el que filtrar.
   * @returns {Promise<ApiResponse<Provider[]>>} - Una promesa que se resuelve con la lista de proveedores.
   */
  async getProviders(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<ApiResponse<Provider[]>> {
    const queryString = params ? `?${new URLSearchParams(params as Record<string, string>).toString()}` : '';
    return this.get(`${API_CONSTANTS.ENDPOINTS.PROVIDERS.LIST}${queryString}`);
  }

  async getProvider(id: string): Promise<ApiResponse<Provider>> {
    return this.get(`${API_CONSTANTS.ENDPOINTS.PROVIDERS.LIST}/${id}`);
  }

  async createProvider(provider: Omit<Provider, 'id'>): Promise<ApiResponse<Provider>> {
    return this.post(API_CONSTANTS.ENDPOINTS.PROVIDERS.CREATE, provider);
  }

  async updateProvider(id: string, provider: Partial<Provider>): Promise<ApiResponse<Provider>> {
    return this.put(`${API_CONSTANTS.ENDPOINTS.PROVIDERS.UPDATE.replace(':id', id)}`, provider);
  }

  async deleteProvider(id: string): Promise<ApiResponse<void>> {
    return this.delete(`${API_CONSTANTS.ENDPOINTS.PROVIDERS.DELETE.replace(':id', id)}`);
  }
}

// Instancias de servicios
export const authService = new AuthService();
export const autofacturaService = new AutofacturaService();
export const gastosService = new GastosService();
export const proveedoresService = new ProveedoresService();
